/**
 * Spec 218 PR4 — Emit-invoice form (`/invoices/new`).
 *
 * Web port of `mobile/src/screens/EmitInvoiceScreen.tsx`. Same data
 * contract: collects an `InvoiceDraft` (series, number, type, recipient,
 * lines) and forwards it to /invoices/new/review via router state.
 *
 * Differences vs mobile:
 *   - Single page (no review tab) — the review screen is a separate
 *     route in this flow so the user has a clear back/forward URL.
 *   - HTML form with native validation messages + per-field aria-invalid;
 *     the inline error block surfaces the first failure on submit.
 *   - Lines are kept editable as a flat array; the user picks the
 *     canonical field (base or total) implicitly by clicking into it,
 *     same as mobile.
 *
 * Guards:
 *   - When the user has no fiscal profile yet, the route bounces to
 *     /settings/fiscal with a friendly notice. Verifacti rejects an
 *     emit without a registered NIF, so we spare the round-trip.
 *   - When the relay reports `verifacti=false` (feature flag off),
 *     the route renders an info notice with a link back to the list.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, HelpCircle, Plus, Trash2, AlertTriangle } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import {
  getFiscalProfile,
  isVerifactiEnabled,
  listInvoices,
  type InvoicingCtx,
} from '../sync/invoicing';
import {
  recomputeInvoiceLine,
  type InvoiceLineCanonical,
} from '../utils/invoiceMath';
import type {
  ForeignIdType,
  Invoice,
  InvoiceDraft,
  InvoiceLine,
  InvoiceRecipient,
  InvoiceType,
  RecipientKind,
} from '@cuadrilla/types/invoice';
import type { WebIdentity } from '../store/keys';

import './EmitInvoice.css';

interface EmitInvoiceProps {
  identity: WebIdentity;
}

interface KindOption {
  kind: RecipientKind;
  label: string;
}

interface TypeOption {
  type: InvoiceType;
  label: string;
}

const KIND_OPTIONS: ReadonlyArray<KindOption> = [
  { kind: 'national', label: 'Nacional' },
  { kind: 'eu', label: 'UE' },
  { kind: 'international', label: 'Internacional' },
  { kind: 'none', label: 'Sin destinatario' },
];

const TYPE_OPTIONS: ReadonlyArray<TypeOption> = [
  { type: 'F1', label: 'F1 — Factura' },
  { type: 'F2', label: 'F2 — Simplificada' },
];

const RECTIFY_TYPE_OPTIONS: ReadonlyArray<TypeOption> = [
  { type: 'R1', label: 'R1 — Error fundamentado' },
  { type: 'R2', label: 'R2 — Concurso' },
  { type: 'R3', label: 'R3 — Incobrables' },
  { type: 'R4', label: 'R4 — Otra' },
  { type: 'R5', label: 'R5 — Simplificada' },
];

const FOREIGN_ID_OPTIONS: ReadonlyArray<{ id: ForeignIdType; label: string }> = [
  { id: '02', label: 'NIF-IVA' },
  { id: '03', label: 'Documento país' },
  { id: '04', label: 'Pasaporte' },
  { id: '06', label: 'Otro' },
];

interface EditableLine extends InvoiceLine {
  canonical: InvoiceLineCanonical;
  total: number;
}

function emptyLine(): EditableLine {
  return {
    description: '',
    base: 0,
    vatRate: 21,
    vatAmount: 0,
    total: 0,
    canonical: 'base',
    taxClass: 'S1',
    regimeKey: '01',
    taxType: '01',
  };
}

interface NavState {
  rectificationOf?: Invoice;
  /**
   * When the user clicks "Volver" from /invoices/new/review, the review
   * route hands the draft back so we re-hydrate the form instead of
   * dropping the user's typing on the floor. Takes precedence over
   * the `rectificationOf` seed when both are present (e.g. user
   * rectifying, then went to review and came back).
   */
  draft?: InvoiceDraft;
}

function draftToEditableLines(lines: ReadonlyArray<InvoiceLine>): EditableLine[] {
  return lines.map((l) => {
    const total = Number((l.base + l.vatAmount).toFixed(2));
    return { ...l, canonical: 'base' as const, total };
  });
}

function buildCtx(identity: WebIdentity): InvoicingCtx {
  return { companyToken: identity.companyToken };
}

function nextSeriesNumber(invoices: ReadonlyArray<{ series: string; number: string }>): {
  series: string;
  number: string;
} {
  const year = new Date().getFullYear();
  if (invoices.length === 0) return { series: String(year), number: '1' };
  const top = invoices
    .filter((inv) => inv.series === String(year))
    .map((inv) => Number(inv.number))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => b - a)[0];
  const nextNumber = top ? top + 1 : 1;
  return { series: String(year), number: String(nextNumber) };
}

export function EmitInvoice({ identity }: EmitInvoiceProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state ?? null) as NavState | null;
  const rectificationOf = navState?.rectificationOf;
  const incomingDraft = navState?.draft;
  const isRectifying = Boolean(rectificationOf);

  const [bootstrap, setBootstrap] = useState<
    | { state: 'loading' }
    | { state: 'no-feature' }
    | { state: 'no-profile' }
    | { state: 'ready' }
    | { state: 'error'; message: string }
  >({ state: 'loading' });

  const [series, setSeries] = useState(incomingDraft?.series ?? '');
  const [numberStr, setNumberStr] = useState(incomingDraft?.number ?? '');
  const [issueDate, setIssueDate] = useState(
    () => incomingDraft?.issueDate ?? new Date().toISOString().slice(0, 10),
  );
  const [type, setType] = useState<InvoiceType>(
    incomingDraft?.type ?? (isRectifying ? 'R1' : 'F1'),
  );
  const [description, setDescription] = useState(incomingDraft?.description ?? '');
  const [recipient, setRecipient] = useState<InvoiceRecipient>(
    incomingDraft?.recipient ?? {
      kind: 'national',
      name: '',
      nif: '',
      address: '',
      email: '',
    },
  );
  const [lines, setLines] = useState<EditableLine[]>(() =>
    incomingDraft ? draftToEditableLines(incomingDraft.lines) : [emptyLine()],
  );
  const [showRectifyHelp, setShowRectifyHelp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeOptions = isRectifying ? RECTIFY_TYPE_OPTIONS : TYPE_OPTIONS;

  // Bootstrap: feature flag → fiscal profile → next series/number.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ctx = buildCtx(identity);
        const enabled = await isVerifactiEnabled(ctx);
        if (cancelled) return;
        if (!enabled) {
          setBootstrap({ state: 'no-feature' });
          return;
        }
        const profile = await getFiscalProfile(ctx);
        if (cancelled) return;
        if (!profile) {
          setBootstrap({ state: 'no-profile' });
          return;
        }
        // Incoming draft (back-from-review) skips the listInvoices call —
        // the form is already populated from the draft we got handed.
        if (incomingDraft) {
          setBootstrap({ state: 'ready' });
          return;
        }
        const invoices = await listInvoices(ctx);
        if (cancelled) return;
        const seed = rectificationOf
          ? { series: rectificationOf.series, number: String(Number(rectificationOf.number) + 1) }
          : nextSeriesNumber(invoices);
        setSeries(seed.series);
        setNumberStr(seed.number);
        if (rectificationOf) {
          setDescription(`Rectifica ${rectificationOf.series}-${rectificationOf.number}: ${rectificationOf.description}`);
          setRecipient(rectificationOf.recipient);
          setLines(draftToEditableLines(rectificationOf.lines));
        }
        setBootstrap({ state: 'ready' });
      } catch (err) {
        if (cancelled) return;
        setBootstrap({
          state: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `incomingDraft` and `rectificationOf` come from `location.state` —
    // they're stable for the lifetime of this route mount, which is what
    // we want: bootstrap only runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const totals = useMemo(() => {
    let totalBase = 0;
    let totalVat = 0;
    let total = 0;
    for (const l of lines) {
      totalBase += Number(l.base) || 0;
      totalVat += Number(l.vatAmount) || 0;
      total += Number(l.total) || 0;
    }
    return {
      totalBase: Number(totalBase.toFixed(2)),
      totalVat: Number(totalVat.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  }, [lines]);

  function updateLineDescription(idx: number, value: string) {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], description: value };
      return next;
    });
  }

  function updateLineNumeric(idx: number, field: 'base' | 'vatRate' | 'total', raw: string) {
    const value = Number(raw.replace(',', '.')) || 0;
    setLines((prev) => {
      const next = [...prev];
      const current = next[idx];
      const canonical: InvoiceLineCanonical =
        field === 'base' ? 'base' : field === 'total' ? 'total' : current.canonical;
      const recomputed = recomputeInvoiceLine({
        base: field === 'base' ? value : current.base,
        vatRate: field === 'vatRate' ? value : current.vatRate,
        total: field === 'total' ? value : current.total,
        canonical,
      });
      next[idx] = { ...current, ...recomputed, canonical };
      return next;
    });
  }

  function setCanonical(idx: number, canonical: InvoiceLineCanonical) {
    setLines((prev) => {
      const next = [...prev];
      const current = next[idx];
      if (current.canonical === canonical) return prev;
      const recomputed = recomputeInvoiceLine({
        base: current.base,
        vatRate: current.vatRate,
        total: current.total,
        canonical,
      });
      next[idx] = { ...current, ...recomputed, canonical };
      return next;
    });
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine()]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!series.trim() || !numberStr.trim()) {
      setError('Serie y número son obligatorios.');
      return;
    }
    if (!description.trim()) {
      setError('La descripción es obligatoria.');
      return;
    }
    if (recipient.kind !== 'none') {
      if (!recipient.name?.trim()) {
        setError('El nombre del destinatario es obligatorio.');
        return;
      }
      if (recipient.kind === 'national' && !recipient.nif?.trim()) {
        setError('El NIF del destinatario es obligatorio.');
        return;
      }
    }
    if (lines.length === 0 || lines.some((l) => !l.description.trim() || !l.base)) {
      setError('Cada línea necesita descripción y un importe base.');
      return;
    }

    const draft: InvoiceDraft = {
      series: series.trim(),
      number: numberStr.trim(),
      issueDate,
      type,
      description: description.trim(),
      recipient,
      lines: lines.map(({ canonical, total: lineTotal, ...rest }) => {
        const r = recomputeInvoiceLine({
          base: rest.base,
          vatRate: rest.vatRate,
          total: lineTotal,
          canonical,
        });
        return { ...rest, base: r.base, vatRate: r.vatRate, vatAmount: r.vatAmount };
      }),
    };

    if (rectificationOf) {
      draft.rectification = {
        method: 'S',
        originalInvoiceId: rectificationOf.id,
        rectifiedBase: Number(rectificationOf.totalBase),
        rectifiedVat: Number(rectificationOf.totalVat),
      };
    }

    navigate('/invoices/new/review', { state: { draft } });
  }

  if (bootstrap.state === 'loading') {
    return (
      <main className="ei-shell" data-testid="emit-invoice-loading">
        <SkeletonList count={6} />
      </main>
    );
  }

  if (bootstrap.state === 'no-feature') {
    return (
      <main className="ei-shell" data-testid="emit-invoice-no-feature">
        <header className="ei-header">
          <Link to="/invoices" className="ei-back" data-testid="emit-invoice-back">
            <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" /> Volver
          </Link>
        </header>
        <EmptyState
          variant="noResults"
          title="Facturación no disponible"
          body={<>El servicio de Verifactu no está activado en este servidor.</>}
        />
      </main>
    );
  }

  if (bootstrap.state === 'no-profile') {
    return (
      <main className="ei-shell" data-testid="emit-invoice-no-profile">
        <header className="ei-header">
          <Link to="/invoices" className="ei-back" data-testid="emit-invoice-back">
            <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" /> Volver
          </Link>
        </header>
        <EmptyState
          variant="noResults"
          title="Configura tu perfil fiscal"
          body={
            <>
              Para emitir facturas necesitas registrar el NIF, razón social y dirección de la empresa.
            </>
          }
          action={{
            label: 'Configurar perfil fiscal',
            onClick: () => navigate('/settings/fiscal'),
            testId: 'emit-invoice-go-to-fiscal',
          }}
        />
      </main>
    );
  }

  if (bootstrap.state === 'error') {
    return (
      <main className="ei-shell" data-testid="emit-invoice-error">
        <EmptyState
          variant="noResults"
          title="No se pudo cargar"
          body={<>{bootstrap.message}. Vuelve a intentarlo más tarde.</>}
        />
      </main>
    );
  }

  return (
    <main className="ei-shell" data-testid="emit-invoice-screen">
      <header className="ei-header">
        <Link to="/invoices" className="ei-back" data-testid="emit-invoice-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </Link>
        <h1 className="ei-h1" data-testid="emit-invoice-title">
          {isRectifying ? 'Rectificar factura' : 'Emitir factura'}
        </h1>
      </header>

      <form className="ei-form" onSubmit={submit} data-testid="emit-invoice-form" noValidate>
        {isRectifying && rectificationOf && (
          <div className="ei-warn" data-testid="emit-invoice-rectify-banner">
            <AlertTriangle size={20} strokeWidth={2} aria-hidden="true" />
            <span>
              Esta factura rectifica a <strong>{rectificationOf.series}-{rectificationOf.number}</strong>.
            </span>
          </div>
        )}

        <div className="ei-row">
          <label className="ei-field ei-field-narrow">
            <span className="ei-label">Serie</span>
            <input
              type="text"
              className="ei-input"
              value={series}
              onChange={(e) => setSeries(e.target.value.toUpperCase())}
              data-testid="emit-invoice-series"
              placeholder="2026"
              autoComplete="off"
            />
          </label>
          <label className="ei-field">
            <span className="ei-label">Número</span>
            <input
              type="text"
              className="ei-input"
              value={numberStr}
              onChange={(e) => setNumberStr(e.target.value)}
              data-testid="emit-invoice-number"
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
        </div>

        <label className="ei-field">
          <span className="ei-label">Fecha de emisión</span>
          <input
            type="date"
            className="ei-input"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            data-testid="emit-invoice-issue-date"
          />
        </label>

        <fieldset className="ei-fieldset">
          <legend className="ei-legend">
            Tipo
            {isRectifying && (
              <button
                type="button"
                className="ei-help-btn"
                onClick={() => setShowRectifyHelp((v) => !v)}
                aria-label="Ayuda sobre tipos de rectificativa"
                data-testid="emit-invoice-rectify-help"
              >
                <HelpCircle size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </legend>
          {showRectifyHelp && (
            <p className="ei-hint" data-testid="emit-invoice-rectify-help-body">
              R1 corrige errores fundamentados en derecho. R2 aplica concurso de acreedores. R3 cubre créditos
              incobrables. R4 cualquier otra rectificación. R5 corrige una factura simplificada (F2).
            </p>
          )}
          <div className="ei-pills" role="radiogroup">
            {typeOptions.map((opt) => (
              <button
                type="button"
                key={opt.type}
                className={`ei-pill${type === opt.type ? ' ei-pill-active' : ''}`}
                onClick={() => setType(opt.type)}
                role="radio"
                aria-checked={type === opt.type}
                data-testid={`emit-invoice-type-${opt.type}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="ei-field">
          <span className="ei-label">Descripción / concepto</span>
          <textarea
            className="ei-input ei-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="emit-invoice-concept"
            rows={2}
          />
          <span className="ei-hint">Aparece tanto en la factura como en el envío a la AEAT.</span>
        </label>

        <fieldset className="ei-fieldset">
          <legend className="ei-legend">Tipo de destinatario</legend>
          <div className="ei-pills" role="radiogroup">
            {KIND_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.kind}
                className={`ei-pill${recipient.kind === opt.kind ? ' ei-pill-active' : ''}`}
                onClick={() =>
                  setRecipient((prev) => ({
                    ...prev,
                    kind: opt.kind,
                    // Clear NIF/foreign fields when switching kind so stale data
                    // doesn't leak into the next emission.
                    nif: opt.kind === 'national' ? prev.nif : undefined,
                    countryCode: opt.kind === 'eu' || opt.kind === 'international' ? prev.countryCode : undefined,
                    foreignIdType: opt.kind === 'eu' || opt.kind === 'international' ? prev.foreignIdType : undefined,
                    foreignId: opt.kind === 'eu' || opt.kind === 'international' ? prev.foreignId : undefined,
                  }))
                }
                role="radio"
                aria-checked={recipient.kind === opt.kind}
                data-testid={`emit-invoice-recipient-kind-${opt.kind}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        {recipient.kind !== 'none' && (
          <label className="ei-field">
            <span className="ei-label">Nombre / razón social</span>
            <input
              type="text"
              className="ei-input"
              value={recipient.name ?? ''}
              onChange={(e) => setRecipient((prev) => ({ ...prev, name: e.target.value }))}
              data-testid="emit-invoice-recipient-name"
              autoComplete="off"
            />
          </label>
        )}
        {recipient.kind === 'national' && (
          <label className="ei-field">
            <span className="ei-label">NIF / CIF</span>
            <input
              type="text"
              className="ei-input"
              value={recipient.nif ?? ''}
              onChange={(e) => setRecipient((prev) => ({ ...prev, nif: e.target.value.toUpperCase() }))}
              data-testid="emit-invoice-recipient-nif"
              maxLength={9}
              autoComplete="off"
            />
          </label>
        )}
        {(recipient.kind === 'eu' || recipient.kind === 'international') && (
          <>
            <label className="ei-field">
              <span className="ei-label">País (ISO)</span>
              <input
                type="text"
                className="ei-input"
                value={recipient.countryCode ?? ''}
                onChange={(e) =>
                  setRecipient((prev) => ({ ...prev, countryCode: e.target.value.toUpperCase() }))
                }
                data-testid="emit-invoice-recipient-country"
                maxLength={2}
                placeholder="FR"
                autoComplete="off"
              />
            </label>
            <fieldset className="ei-fieldset">
              <legend className="ei-legend">Tipo de documento</legend>
              <div className="ei-pills" role="radiogroup">
                {FOREIGN_ID_OPTIONS.map((opt) => (
                  <button
                    type="button"
                    key={opt.id}
                    className={`ei-pill${recipient.foreignIdType === opt.id ? ' ei-pill-active' : ''}`}
                    onClick={() => setRecipient((prev) => ({ ...prev, foreignIdType: opt.id }))}
                    role="radio"
                    aria-checked={recipient.foreignIdType === opt.id}
                    data-testid={`emit-invoice-recipient-fid-type-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <label className="ei-field">
              <span className="ei-label">Identificador</span>
              <input
                type="text"
                className="ei-input"
                value={recipient.foreignId ?? ''}
                onChange={(e) => setRecipient((prev) => ({ ...prev, foreignId: e.target.value }))}
                data-testid="emit-invoice-recipient-foreign-id"
                autoComplete="off"
              />
            </label>
          </>
        )}
        {recipient.kind !== 'none' && (
          <label className="ei-field">
            <span className="ei-label">Email (opcional)</span>
            <input
              type="email"
              className="ei-input"
              value={recipient.email ?? ''}
              onChange={(e) => setRecipient((prev) => ({ ...prev, email: e.target.value }))}
              data-testid="emit-invoice-recipient-email"
              autoComplete="off"
            />
            <span className="ei-hint">No se envía a la AEAT. Solo lo usamos para enviarte la factura.</span>
          </label>
        )}

        <h2 className="ei-section-title">Líneas</h2>
        {lines.map((line, idx) => {
          const baseCanonical = line.canonical === 'base';
          const totalCanonical = line.canonical === 'total';
          return (
            <div key={idx} className="ei-line" data-testid={`emit-invoice-line-${idx}`}>
              <input
                type="text"
                className="ei-input"
                value={line.description}
                onChange={(e) => updateLineDescription(idx, e.target.value)}
                placeholder="Descripción de la línea"
                data-testid={`emit-invoice-line-description-${idx}`}
                autoComplete="off"
              />
              <div className="ei-row ei-line-numbers">
                <label className="ei-field ei-field-narrow">
                  <span className={`ei-label${baseCanonical ? ' ei-label-canonical' : ''}`}>Base</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`ei-input${baseCanonical ? ' ei-input-canonical' : ''}`}
                    value={String(line.base)}
                    onFocus={() => setCanonical(idx, 'base')}
                    onChange={(e) => updateLineNumeric(idx, 'base', e.target.value)}
                    data-testid={`emit-invoice-line-base-${idx}`}
                    autoComplete="off"
                  />
                </label>
                <label className="ei-field ei-field-narrow">
                  <span className="ei-label">% IVA</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="ei-input"
                    value={String(line.vatRate)}
                    onChange={(e) => updateLineNumeric(idx, 'vatRate', e.target.value)}
                    data-testid={`emit-invoice-line-vat-rate-${idx}`}
                    autoComplete="off"
                  />
                </label>
                <label className="ei-field ei-field-narrow">
                  <span className={`ei-label${totalCanonical ? ' ei-label-canonical' : ''}`}>Total</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`ei-input${totalCanonical ? ' ei-input-canonical' : ''}`}
                    value={String(line.total)}
                    onFocus={() => setCanonical(idx, 'total')}
                    onChange={(e) => updateLineNumeric(idx, 'total', e.target.value)}
                    data-testid={`emit-invoice-line-total-${idx}`}
                    autoComplete="off"
                  />
                </label>
              </div>
              <div className="ei-line-meta">
                <span>
                  Cuota IVA:{' '}
                  <strong data-testid={`emit-invoice-line-vat-amount-${idx}`}>
                    {line.vatAmount.toFixed(2)} €
                  </strong>
                </span>
                <span className="ei-line-canonical-hint">
                  {baseCanonical ? 'Calculado a partir de la base.' : 'Calculado a partir del total.'}
                </span>
              </div>
              <span data-testid={`emit-invoice-line-canonical-${idx}`} className="ei-hidden-marker">
                {line.canonical}
              </span>
              {lines.length > 1 && (
                <button
                  type="button"
                  className="ei-line-remove"
                  onClick={() => removeLine(idx)}
                  data-testid={`emit-invoice-line-remove-${idx}`}
                >
                  <Trash2 size={14} strokeWidth={2} aria-hidden="true" /> Eliminar línea
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="ei-line-add"
          onClick={addLine}
          data-testid="emit-invoice-line-add"
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" /> Añadir línea
        </button>

        <div className="ei-totals" data-testid="emit-invoice-totals">
          <div className="ei-total-row">
            <span>Total base</span>
            <strong data-testid="emit-invoice-total-base">{totals.totalBase.toFixed(2)} €</strong>
          </div>
          <div className="ei-total-row">
            <span>Total IVA</span>
            <strong data-testid="emit-invoice-total-vat">{totals.totalVat.toFixed(2)} €</strong>
          </div>
          <div className="ei-total-row ei-total-row-bold">
            <span>Total</span>
            <strong data-testid="emit-invoice-total">{totals.total.toFixed(2)} €</strong>
          </div>
        </div>

        {error && (
          <p className="ei-error" role="alert" data-testid="emit-invoice-error">
            {error}
          </p>
        )}

        <div className="ei-actions">
          <button
            type="submit"
            className="ei-primary"
            data-testid="emit-invoice-submit"
            disabled={bootstrap.state !== 'ready'}
          >
            Continuar a revisión
          </button>
        </div>
      </form>
    </main>
  );
}
